import React, { useRef, useState } from "react";
import {
  Button,
  LoadingIndicator,
  LoadingOverlay,
  Skeleton,
  Snackbar,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import { useParams } from "react-router-dom";
import styled from "@emotion/styled/macro";
import { useMediaQuery } from "react-responsive";
import { rollbar } from "../../../providers/rollbar";
import { useAppSelector } from "../../../store";
import { HomeReadinessStepView } from "../organisms/HomeReadinessStepView";
import { ReadSignStepView } from "../organisms/ReadSignStepView";
import { DesignStepView } from "../organisms/DesignStepView/DesignStepView";
import { useCapContext } from "../../providers/CapContext";
import heroImg from "../../assets/hero-image.svg";
import triangleOverlay from "../../assets/triangle_overlay.svg";
import { CapNav } from "../molecules/CapNav";
import { CapSimplifiedNav } from "../molecules/CapSimplifiedNav";
import { NextSteps } from "../organisms/NextStepsStepView";
import { EquipmentLocationStepView } from "../organisms/EquipmentLocationStepView";
import { StepName } from "../../capTypes";
import { getVisibleCapSteps, STEPS } from "../../constants/capConstants";
import { scrollToCapStep, useCapScrollSpy } from "../../hooks/useCapScrollSpy";
import {
  CapLegacyStickyNavInner,
  CapLegacyStickyNavShell,
  CapSimplifiedStickyNavInner,
  CapSimplifiedStickyNavShell,
  CapStepSection,
  CapStickyNavSentinel,
  getCapStickyNavOffset,
  useCapNavCompact,
  useCapStickyNavStuck,
} from "../molecules/CapStepSection";
import { MobileAppDownloadBanner } from "../molecules/MobileAppDownloadBanner";
import { SharePresentationModal } from "../molecules/SharePresentationModal";
import { CapClassicViewIndicator } from "../molecules/CapClassicViewIndicator";
import { CapSimplifiedPageHeader } from "../organisms/CapSimplifiedPageHeader";
import { ErrorModal } from "components/molecules/ErrorModal";
import {
  ContactsUnavailableModal,
  isProspectNotQualifiedContactsError,
} from "components/organisms/ContactsUnavailableModal";
import { ProspectUnavailableModal } from "components/organisms/ProspectUnavailableModal";
import { isProspectUnavailable } from "utils/contactsErrors";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";
import { MaintenanceBanner } from "checkout/components/molecules/MaintenanceBanner";
import { sendProposalMessageAndEmail } from "services/shareProposal";

type CapPresentationProps = {
  stepName: StepName;
};

const CapPresentationContent = ({ stepName }: CapPresentationProps) => {
  switch (stepName) {
    case STEPS.SYSTEM_DESIGN:
      return <DesignStepView />;
    case STEPS.HOME_READINESS:
      return <HomeReadinessStepView />;
    case STEPS.READ_SIGN:
      return <ReadSignStepView />;
    case STEPS.NEXT_STEPS:
      return <NextSteps />;
    case STEPS.EQUIPMENT_LOCATION:
      return <EquipmentLocationStepView />;
    default:
      return <div>Unknown Step Name</div>;
  }
};

const CapPage: React.FC = () => {
  const { prospectId = "" } = useParams();
  const hybridToken = useAppSelector((state) => state?.auth?.hybridToken);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isPresentationSending, setIsPresentationSending] = useState(false);
  const [snackbarState, setSnackbarState] = useState<{
    message: string;
    type: "success" | "error";
    show: boolean;
  }>({
    message: "",
    type: "success",
    show: false,
  });
  const [currentStep, changeStep] = useState<StepName>(STEPS.SYSTEM_DESIGN);
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  const {
    activeProposal,
    activeProposalLoading,
    cap,
    contacts,
    contactsError,
    equipmentLocationImages,
    isContactsLoading,
    isSowLoading,
    isNonSolar,
    isOnlyBattery,
    isOnlyEvCharger,
    prospect,
    prospectError,
    prospectLoading,
    originalProposalDesignImagesLoading,
    sowError,
    isCapLoading,
    userProfile,
    isCapSimplifiedExperience: showCapSimplifiedExperience,
    isCapSimplifiedGatePending,
  } = useCapContext();

  const visibleSteps = getVisibleCapSteps(cap, equipmentLocationImages);
  const visibleStepNames = visibleSteps.map((step) => step.name);
  const isCompact = useCapNavCompact();
  const stickyNavSentinelRef = useRef<HTMLDivElement>(null);

  const isStickyNavStuck = useCapStickyNavStuck(
    stickyNavSentinelRef,
    showCapSimplifiedExperience
  );
  const isCompactNav = showCapSimplifiedExperience
    ? isStickyNavStuck
    : isCompact;
  const navOffset = getCapStickyNavOffset(isCompactNav);

  useCapScrollSpy(visibleStepNames, currentStep, changeStep, navOffset);

  if (sowError) {
    return (
      <ErrorModal
        context="CapPage"
        error={sowError}
        action={"loading Cap SOW"}
      />
    );
  }

  if (
    isProspectUnavailable({
      prospect,
      prospectError,
      prospectLoading,
    })
  ) {
    return <ProspectUnavailableModal />;
  }

  if (contactsError && prospectLoading) {
    return <LoadingIndicator fullScreen color="black" />;
  }

  if (contactsError) {
    if (isProspectNotQualifiedContactsError(contactsError)) {
      return (
        <ContactsUnavailableModal
          prospect={prospect}
          userProfile={userProfile}
          errorMessage={contactsError?.response?.body?.message}
        />
      );
    }

    return (
      <ErrorModal
        context="CapPage"
        error={contactsError}
        action="Get Contacts"
      />
    );
  }

  const showAutopay =
    activeProposal?.dealType === "FlexMonthly PPA" ||
    activeProposal?.dealType === "FlexMonthly" ||
    activeProposal?.dealType === "monthly";

  const primaryContact = contacts?.filter((c) => c.primary)[0];

  // Hold the skeleton until the simplified gate resolves so reps don't see
  // the legacy page flash before it switches to the simplified layout.
  const isLoading =
    !contacts ||
    isContactsLoading ||
    activeProposalLoading ||
    isSowLoading ||
    prospectLoading ||
    originalProposalDesignImagesLoading ||
    isCapLoading ||
    isCapSimplifiedGatePending;

  const handleSendPresentation = async () => {
    setIsPresentationSending(true);
    setShowShareModal(false);
    try {
      await sendProposalMessageAndEmail(hybridToken, prospectId, "", true);
      setSnackbarState({
        show: true,
        message: "CAP presentation sent successfully!",
        type: "success",
      });
    } catch (error) {
      rollbar.error(error as Error);
      setSnackbarState({
        show: true,
        message: "Failed to send CAP presentation",
        type: "error",
      });
    } finally {
      setIsPresentationSending(false);
    }
  };

  const getHeaderText = () => {
    if (isOnlyBattery) {
      return `${primaryContact?.customerFirstName}'s storage project`;
    }
    if (isOnlyEvCharger) {
      return `${primaryContact?.customerFirstName}'s EV charger`;
    }
    return `${primaryContact?.customerFirstName}'s solar project`;
  };

  const CapPageHeaderContainer = () => {
    return isLoading ? (
      <LoadingCapPageHeader />
    ) : (
      <CapPageHeader isMobile={isMobile} isTablet={isTablet}>
        {!isMobile && <TriangleOverlay src={triangleOverlay} />}
        <CapClassicViewIndicator isMobile={isMobile} isTablet={isTablet} />
        <HeaderContentContainer isMobile={isMobile} isTablet={isTablet}>
          {currentStep === STEPS.READ_SIGN && showAutopay && (
            <MaintenanceBannerContainer>
              <MaintenanceBanner />
            </MaintenanceBannerContainer>
          )}
          <Typography
            style={{ zIndex: 2, position: "relative" }}
            color={tokens.TINTS_OFF_WHITE_90}
            size={isTablet ? 20 : 32}
            fontWeight={"regular"}
            lineHeight={isTablet ? 26 : 38}
          >
            Final design approval
          </Typography>
          <Typography
            style={{ zIndex: 2, position: "relative" }}
            color={tokens.TINTS_OFF_WHITE_90}
            size={isTablet ? 40 : 72}
            lineHeight={isTablet ? 48 : 80}
            fontWeight={"regular"}
          >
            {getHeaderText()}
          </Typography>
        </HeaderContentContainer>
      </CapPageHeader>
    );
  };

  const scrollToReadSignDocs = () => {
    changeStep(STEPS.READ_SIGN);
    scrollToCapStep(STEPS.READ_SIGN, navOffset);
  };

  const capMainContent = (
    <>
      {showCapSimplifiedExperience ? (
        <>
          <CapStickyNavSentinel ref={stickyNavSentinelRef} aria-hidden />
          <CapSimplifiedStickyNavShell $isStuck={isStickyNavStuck}>
            <CapSimplifiedStickyNavInner
              isMobile={isMobile}
              isTablet={isTablet}
            >
              <CapSimplifiedNav
                isCompact={isCompactNav}
                isStickyNavPinned={isStickyNavStuck}
                isLoading={isLoading}
                changeStep={changeStep}
                currentStep={currentStep}
                visibleSteps={visibleSteps}
              />
            </CapSimplifiedStickyNavInner>
          </CapSimplifiedStickyNavShell>
        </>
      ) : (
        <CapLegacyStickyNavShell>
          <CapLegacyStickyNavInner isMobile={isMobile} isTablet={isTablet}>
            <CapNav
              isCompact={isCompactNav}
              isLoading={isLoading}
              changeStep={changeStep}
              currentStep={currentStep}
              visibleSteps={visibleSteps}
            />
          </CapLegacyStickyNavInner>
        </CapLegacyStickyNavShell>
      )}
      <CapPageContainer
        isTablet={isTablet}
        isMobile={isMobile}
        $simplified={showCapSimplifiedExperience}
      >
        <ContentContainer $simplified={showCapSimplifiedExperience}>
          {visibleSteps.map((step) => (
            <CapStepSection
              key={step.name}
              data-cap-step={step.name}
              isCompact={isCompactNav}
            >
              <CapPresentationContent stepName={step.name} />
            </CapStepSection>
          ))}
        </ContentContainer>
      </CapPageContainer>
      {(currentStep === STEPS.NEXT_STEPS ||
        (currentStep === STEPS.READ_SIGN && showAutopay)) && (
        <MobileAppDownloadBanner pageContext="CAP" />
      )}
      {isLoading ? (
        <FooterSkeleton />
      ) : (
        <Footer isTablet={isTablet} $simplified={showCapSimplifiedExperience}>
          <FooterText isTablet={isTablet} isMobile={isMobile}>
            <Typography
              size={isMobile ? 24 : 32}
              color={tokens.WHITE}
              fontWeight={isTablet ? "medium" : "regular"}
            >
              Take your next step towards{" "}
              {isNonSolar ? "energy storage" : "solar"}
            </Typography>
          </FooterText>
          {!showCapSimplifiedExperience ? (
            <FooterButtons isTablet={isTablet}>
              {Boolean(userProfile) && (
                <EmailButton
                  isTablet={isTablet}
                  onClick={() => setShowShareModal(true)}
                >
                  Share presentation
                </EmailButton>
              )}
              {currentStep !== STEPS.READ_SIGN && (
                <AgreementsButton
                  isTablet={isTablet}
                  onClick={() => scrollToReadSignDocs()}
                >
                  Read & Sign Docs
                </AgreementsButton>
              )}
            </FooterButtons>
          ) : null}
        </Footer>
      )}
    </>
  );

  return (
    <>
      {showCapSimplifiedExperience ? (
        <CapPageSimplifiedShell>
          <CapSimplifiedPageHeader
            isLoading={isLoading}
            isMobile={isMobile}
            showSharePresentation={Boolean(userProfile)}
            showReadSignDocs={currentStep !== STEPS.READ_SIGN}
            onSharePresentation={() => setShowShareModal(true)}
            onReadSignDocs={scrollToReadSignDocs}
          />
          {capMainContent}
        </CapPageSimplifiedShell>
      ) : (
        <>
          <CapPageHeaderContainer />
          {capMainContent}
        </>
      )}

      <Snackbar
        onClose={() => setSnackbarState((prev) => ({ ...prev, show: false }))}
        open={snackbarState.show}
        message={snackbarState.message}
        type={snackbarState.type}
      />

      {isPresentationSending && (
        <LoadingOverlay
          message="Sending CAP presentation..."
          data-testid="cap-page-loading-overlay"
        />
      )}

      {showShareModal && (
        <SharePresentationModal
          onClose={() => setShowShareModal(false)}
          onSend={() => handleSendPresentation()}
          customerName={primaryContact?.customerFirstName || ""}
          customerEmail={primaryContact?.customerEmail || ""}
          isSending={isPresentationSending}
        />
      )}
    </>
  );
};

const MaintenanceBannerContainer = styled.div`
  width: 100%;
  margin-bottom: 16px;
`;

const CapPageSimplifiedShell = styled.div`
  background-color: ${tokens.BRAND_OFF_WHITE};
  background-image: linear-gradient(
    180deg,
    ${tokens.BRAND_CHARGE_BLUE} 0px,
    ${tokens.BRAND_OFF_WHITE} 900px
  );
  min-height: 100vh;
`;

const CapPageContainer = styled.section<{
  isTablet: boolean;
  isMobile: boolean;
  $simplified?: boolean;
}>`
  display: flex;
  flex-direction: column;
  padding: ${({ isMobile, isTablet, $simplified }) =>
    $simplified
      ? isMobile
        ? "8px 16px 32px"
        : isTablet
        ? "8px 24px 40px"
        : "8px 64px 64px"
      : isMobile
      ? "24px 16px"
      : isTablet
      ? "16px 24px"
      : "40px 64px 64px"};
  background: ${({ $simplified }) =>
    $simplified
      ? "transparent"
      : `linear-gradient(
    180deg,
    ${tokens.BACKGROUND_LIGHT} 0,
    ${tokens.CHARGEBLUE_50} 60%,
    ${tokens.BACKGROUND_LIGHT} 90%
  )`};
  min-height: ${({ $simplified }) => ($simplified ? "0" : "550px")};
  align-items: center;
`;

const CapPageHeader = styled.header<{ isMobile: boolean; isTablet: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: end;
  background-image: url(${heroImg});
  background-size: cover;
  position: relative;
  height: ${({ isMobile, isTablet }) =>
    isMobile ? "initial" : isTablet ? "initial" : "324px"};
`;

const HeaderContentContainer = styled.div<{
  isMobile: boolean;
  isTablet: boolean;
}>`
  padding: ${({ isMobile, isTablet }) =>
    isMobile ? "16px" : isTablet ? "24px" : "40px 64px"};
  width: 100%;
  box-sizing: border-box;
`;

const LoadingCapPageHeader = styled.header`
  height: 324px;
  position: relative;
  background-color: #d4d5da;
`;

const TriangleOverlay = styled.img`
  position: absolute;
  height: 90%;
  z-index: 1;
  bottom: 0;
  right: 0;

  @media screen and (max-width: 768px) {
    height: 50%;
  }
`;

const FooterSkeleton = styled(Skeleton)`
  width: 1133px;
  height: 156px;
`;

const FooterText = styled.div<{ isTablet: boolean; isMobile: boolean }>`
  display: flex;
  justify-content: center;

  > p {
    text-align: ${({ isTablet }) => (isTablet ? "center" : "inherit")};
    width: ${({ isTablet, isMobile }) =>
      isMobile ? "210px" : isTablet ? "100%" : "484px"};
  }
`;

const FooterButtons = styled.div<{ isTablet: boolean }>`
  width: 100%;
  display: ${({ isTablet }) => (isTablet ? "unset" : "flex")};
  justify-content: ${({ isTablet }) => (isTablet ? "unset" : "flex-end")};
`;

const Footer = styled.div<{ isTablet: boolean; $simplified?: boolean }>`
  background: ${tokens.BRAND_HERO_BLUE};
  width: 100%;
  display: flex;
  align-items: center;
  gap: 32px;
  box-sizing: border-box;
  padding: ${({ isTablet, $simplified }) =>
    $simplified
      ? isTablet
        ? "24px 16px"
        : "32px 64px"
      : isTablet
      ? "24px 16px"
      : "50px"};
  flex-direction: ${({ isTablet }) => (isTablet ? "column" : "unset")};

  @media (min-width: 1700px) {
    min-width: ${({ $simplified }) => ($simplified ? "auto" : "1220px")};
  }
`;

const ContentContainer = styled.section<{ $simplified?: boolean }>`
  max-width: 1200px;
  width: 100%;

  @media (min-width: 1700px) {
    min-width: 1220px;
  }
`;

const EmailButton = styled(Button)<{ isTablet: boolean }>`
  border: 1px solid white;
  height: 62px;
  font-weight: 500;
  margin: ${({ isTablet }) => (isTablet ? "0 0 16px 0" : "0 40px 0 0")};
  width: ${({ isTablet }) => (isTablet ? "100%" : "210px")};
`;

const AgreementsButton = styled(Button)<{ isTablet: boolean }>`
  background: white;
  color: ${tokens.BRAND_HERO_BLUE};
  height: 62px;
  font-weight: 500;
  width: ${({ isTablet }) => (isTablet ? "100%" : "182px")};
`;

export { CapPage };
